import React, { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import Modal from 'react-modal';
import Close from '../../../../resources/images/close.svg';
import AlertTriangle from '../../../../resources/images/alert-triangle.svg';
import Button from '../../../../components/global/atoms/button';
import { ENV_CONFIG } from '../../../../constants/envConfig';
import {
  isAlternateCID,
  convertPhoneNumberAsPerSbmChannel,
  formatPhoneNumberToUSA,
  intl
 } from '../../../../components/global/utils/commonUtils';
import { STORAGE_CONFIG } from '../../../../constants/storageConfig';
import { useUserContext } from '../../../context/UserContext';
import { useCartState } from '../../../../contexts/cart';
import { useCookieValue } from '../../../utils/hooks';

import './rentRestrictionsWarningModal.scss';

const RentRestrictionsWarningModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  const titleRef = useRef(null);
  const contentRef = useRef({
    title: '',
    content: '',
    ctaLabel: '',
  });

  const [{ isProfileLoaded }] = useUserContext();
  const [{ userAccount }] = useCartState();

  const afterOpenModal = () => {
    titleRef?.current?.focus?.();
  };

  const handleOnClose = () => {
    setIsOpen(false);
  };

  const getContent = () => {
    const authoredContent = ENV_CONFIG?.RENT_RESTRICTIONS_WARNING_DATA ?? {};
    if (Object.keys(authoredContent).length > 0) {
      // raw content with placeholders
      const rawContent = authoredContent?.warningpopupmodalcontent || '';

      // process placeholders in the content
      const finalContent = rawContent?.replace(
        /{phone}/g,
        convertPhoneNumberAsPerSbmChannel(formatPhoneNumberToUSA(ENV_CONFIG?.SUPPORT_CONTACT_NUMBER ?? ''))
      );

      contentRef.current.title = authoredContent?.warningpopupmodaltitle || '';
      contentRef.current.content = finalContent || '';
      contentRef.current.ctaLabel = authoredContent?.warningpopupmodalctalabel || '';
    }
  };

  const isBahamasUserLoggedIn = () => {
    // check if company id is 10 or alteriveCompanyId flag is true
    // then the logged in user is a bahamas user
    return isAlternateCID();
  };

  const checkModalVisibilityCookie = () => {
    const cookieName = STORAGE_CONFIG?.COOKIES.RENT_RESTRICTIONS_WARNING_MODAL_DISPLAYED;
    const [cookieValue, setCookieValue] = useCookieValue(cookieName);

    if (cookieValue) {
      const modalClosed = cookieValue?.trim?.() === 'true';
      if (!modalClosed)  {
        return true;
      }
    } else {
      setCookieValue?.('true', 365 * 24 * 60 * 60); // set cookie for 1 year

      return true;
    }

    return false;
  };

  const isAllowedToDisplay = () => {
    const path = window?.location?.href ?? '';
    return !(
      path.includes('/myaccount') || path.includes('/login') || path.includes('/help') || path.includes('/createaccount') || path.includes('/paymentstatus')
    );
  };

  const handleOnProfileLoaded = () => {
    // check if bahamas user logged in
    // display the banner and set the cookie rentRestrictionsWarningModalDisplayed to true
    // otherwise if the cookie is already set or user is not a bahamas user,
    // do not display the modal
    const isBahamasUser = isBahamasUserLoggedIn();
    const isAllowedToDisplayInCurrentPage = isAllowedToDisplay();
    if (isBahamasUser && isAllowedToDisplayInCurrentPage) {
      const shouldDisplayModal = checkModalVisibilityCookie();
      setIsOpen(shouldDisplayModal);
    }
  };

  const init = () => {
    // get authorable content from the DOM
    getContent();
  };

  // initialize the component
  useEffect(init, []);

  // handle on user profile loaded event
  useEffect(() => {
    if (isProfileLoaded) {
      handleOnProfileLoaded();
    }
  }, [isProfileLoaded, userAccount?.accountName]);

  return (
    <Modal isOpen={isOpen} className={`rentRestrictions`} role="none" onAfterOpen={afterOpenModal}>
      <div className={`rentRestrictions__content`}>
        <div className={`rentRestrictions__header`}>
          <div className="rentRestrictions__title-wrap" tabIndex={0}>
            <AlertTriangle className={'rentRestrictions__title-lefticon'} aria-hidden="true" tabIndex={'-1'} />
            <h6 className="rentRestrictions__title" tabIndex={0} ref={titleRef}>
              {contentRef?.current?.title ?? ''}
            </h6>
          </div>
          <Button
            tabIndex={0}
            onClick={handleOnClose}
            buttonAriaLabel={intl.formatMessage({ id: 'rangepicker-tooltip-content' }) || 'hide this message'}
          >
            <Close aria-hidden="true" tabIndex={'-1'} />
          </Button>
        </div>
        <div className={`rentRestrictions__content`}>
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contentRef?.current?.content ?? '')}} />
        </div>
        <div className="rentRestrictions__actions">
          <Button
            type="button"
            className="button button-block button-primary rentRestrictions__button-continue"
            buttonAriaLabel={contentRef?.current?.ctaLabel ?? ''}
            tabIndex={0}
            onClick={handleOnClose}
            dataTestid="rentRestrictionsWarningModalContinueButton"
          >
            {contentRef?.current?.ctaLabel ?? ''}
          </Button>
        </div>
      </div>
    </Modal>
  );
};


export default RentRestrictionsWarningModal;
